import { jsx, jsxs } from "react/jsx-runtime";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTheme, useMediaQuery, Box, styled } from "@mui/material";
import _uniq from "lodash/uniq";
import { FallbackImage } from "../../../Components/FallbackImage.js";
import { CampaignType } from "../../../types/Campaign.js";
import { getAppConsistentParams } from "../../../utils/appUtils.js";
import { resolveProductImageUrls } from "../../../utils/assetUtils.js";
import { getProductUrl, getPurchaseRewardAmountForProduct } from "../../../utils/productUtils.js";
import { decodeHTMLCharacters, formatMoney } from "../../../utils/stringUtils.js";
import { RetailerIconGroup } from "../RetailerIconGroup/index.js";
import { RewardPillWithText } from "../RewardPill/RewardPillWithText.js";
import { MOBILE_SCREEN_SIZE, TABLET_SCREEN_SIZE } from "../../../utils/constants/Constants.js";
const ProductCard = styled("div")(({ theme }) => ({
  border: "1px solid #dcdddc",
  width: "100%",
  height: "100%",
  borderRadius: 12,
  padding: "16px 16px 16px 22px",
  cursor: "pointer",
  boxSizing: "border-box",
  transition: "background-color 0.4s ease-in-out",
  "&:hover": {
    backgroundColor: "#F6F8FA"
  },
  "*": {
    mixBlendMode: "multiply"
  },
  [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: 10,
    width: "100%"
  },
  ".image": {
    width: "calc(100% - 24px)",
    maxWidth: "190px",
    aspectRatio: "1/1",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "16px auto 0",
    img: {
      maxHeight: "100%",
      maxWidth: "100%",
      aspectRatio: "1",
      objectFit: "contain"
    }
  },
  ".title": {
    textDecoration: "none",
    color: theme.vars.palette.text.primary,
    fontSize: 14,
    fontWeight: 500,
    marginTop: 20,
    width: "100%",
    overflow: "hidden",
    letterSpacing: 0.36,
    WebkitLineClamp: 3,
    lineHeight: 1.5,
    height: `4.5em`,
    // line height * 2
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
    userSelect: "text",
    WebkitUserSelect: "text",
    cursor: "text",
    [`@media screen and (max-width: ${1250}px)`]: {
      fontSize: 16
    },
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 12
    }
  },
  ".price": {
    color: theme.vars.palette.text.primary,
    fontSize: 20,
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 10,
    display: "flex",
    columnGap: 12,
    alignItems: "center",
    userSelect: "text",
    WebkitUserSelect: "text",
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 16
    },
    ".value": {
      display: "flex",
      alignItems: "center",
      cursor: "text"
    }
  }
}));
const RewardBarContainer = styled("div", {
  shouldForwardProp: (prop) => !["clickable"].includes(prop.toString())
})(({ clickable, theme }) => ({
  color: theme.vars.palette.text.primary,
  backgroundColor: "#ecf9f6",
  display: "flex",
  alignItems: "center",
  fontWeight: 600,
  padding: "8px 15px",
  borderRadius: 99,
  boxSizing: "border-box",
  fontSize: 14,
  ":hover": {
    cursor: clickable ? "pointer" : "default"
  },
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 8px"
  },
  ".reward_star": {
    width: 12,
    height: 12,
    marginRight: 7
  }
}));
const RewardBar = ({ text, style = {}, onClick, withDots }) => {
  const handleClick = () => {
    if (onClick) onClick();
  };
  return /* @__PURE__ */ jsxs(RewardBarContainer, { style, onClick: handleClick, clickable: !!onClick, children: [
    /* @__PURE__ */ jsx(
      "img",
      {
        loading: "lazy",
        className: "reward_star",
        src: "https://media.brandclub.com/brandclub/image_asset/star-yellow.svg",
        alt: "family_bg"
      }
    ),
    text,
    withDots ? /* @__PURE__ */ jsxs("div", { style: { flex: 1, display: "flex", justifyContent: "flex-end" }, children: [
      /* @__PURE__ */ jsx("div", { style: { minWidth: 10, maxWidth: 50, width: "100%" } }),
      /* @__PURE__ */ jsx(MoreHorizIcon, { sx: (theme) => ({ color: theme.vars.palette.text.primary, fontSize: 16 }) })
    ] }) : null
  ] });
};
const BrandclubProductCard = ({
  product,
  onClick,
  LinkComponent = Box,
  className = "",
  onShareClick,
  targetUrlOverride,
  children
}) => {
  const {
    affiliateRewardAmount,
    hasActiveInstantReward,
    matchedProducts,
    retailerId,
    retailPrice,
    stacklineSku,
    title
  } = product;
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams }, targetUrlOverride);
  const productImage = resolveProductImageUrls(product);
  const cleanProductTitle = decodeHTMLCharacters(title || "");
  const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
  const rewardPercent = !!retailPrice && !!purchaseRewardAmount ? purchaseRewardAmount * 100 / retailPrice : 0;
  const matchedProductsRetailerIds = (matchedProducts ?? []).map((matchedProduct) => matchedProduct.retailerId);
  const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);
  const retailerShortLogoByRetailerId = {};
  if (product.retailerShortLogo) retailerShortLogoByRetailerId[retailerId] = product.retailerShortLogo;
  (matchedProducts ?? []).forEach((m) => {
    if (m.retailerShortLogo) retailerShortLogoByRetailerId[m.retailerId] = m.retailerShortLogo;
  });
  const theme = useTheme();
  const smBreakpoint = theme.breakpoints.values.sm;
  const tabletView = useMediaQuery(`(min-width: ${smBreakpoint}px)`);
  const mobileView = !tabletView;
  const retailerLogoWidth = mobileView ? 15 : 25;
  return /* @__PURE__ */ jsx(LinkComponent, { to: `${targetUrl}`, style: { textDecoration: "none" }, onClick, children: /* @__PURE__ */ jsxs(ProductCard, { className: className + " card-link", children: [
    /* @__PURE__ */ jsx(
      RewardPillWithText,
      {
        onPillClick: onShareClick,
        rewardAmount: affiliateRewardAmount ?? 0,
        rewardCampaignType: CampaignType.AffiliateRewardCampaign,
        containerStyle: { justifyContent: "end" },
        baseFontSize: mobileView ? 11 : 14
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "image", children: /* @__PURE__ */ jsx(
      FallbackImage,
      {
        ...productImage,
        alt: title
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "title", children: cleanProductTitle }),
    /* @__PURE__ */ jsxs("div", { className: "price", children: [
      /* @__PURE__ */ jsx("div", { className: "value", children: formatMoney(retailPrice) }),
      /* @__PURE__ */ jsx(
        RetailerIconGroup,
        {
          retailerIds,
          iconSize: retailerLogoWidth,
          maxSize: 5,
          retailerShortLogoByRetailerId: Object.keys(retailerShortLogoByRetailerId).length > 0 ? retailerShortLogoByRetailerId : void 0
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      RewardPillWithText,
      {
        rewardAmount: purchaseRewardAmount ?? 0,
        rewardCampaignType: hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign,
        rewardPercent,
        rewardPercentText: "reward",
        containerStyle: mobileView ? { columnGap: 8 } : void 0,
        baseFontSize: mobileView ? 11 : 14
      }
    ),
    children
  ] }) });
};
export {
  BrandclubProductCard,
  RewardBar
};
//# sourceMappingURL=index.js.map
